/* eslint-disable @typescript-eslint/no-unsafe-call */
import { Button, Grid } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import * as ol from "ol";
import { Feature } from "ol";
import { Coordinate } from "ol/coordinate";
import { FeatureLike } from "ol/Feature";
import GeometryType from "ol/geom/GeometryType";
import Polygon from "ol/geom/Polygon";
import { Draw, Modify, Snap } from "ol/interaction";
import VectorLayer from "ol/layer/Vector";
import { Fill, Stroke, Style, Text } from "ol/style";
import CircleStyle from "ol/style/Circle";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BerthCreateDto,
  BerthDetailsDto,
  BerthEditDto,
  useBerthApi,
  usePortApi,
} from "~/api/GeneratedApi";
import GenericStepperDialog, {
  DialogStep,
} from "~/components/GenericStepperDialog";
import LoadingBox from "~/components/LoadingBox";
import { addBerthsToMap } from "~/pages/MapPage/functions/addBerthsToMap";
import { addPortsToMap } from "~/pages/MapPage/functions/addPortsToMap";
import { findLayerByTitle } from "~/pages/MapPage/functions/findLayerByTitle";
import { mapToReadOnly } from "~/pages/MapPage/functions/mapToReadOnly";
import { useVectorLayer } from "~/pages/MapPage/functions/useVectorLayer";
import useCreateMap from "~/pages/MapPage/hooks/useCreateMap";
import { LayerTitle } from "~/pages/MapPage/types/layers";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import BerthPortSelectForm from "../components/BerthPortSelectForm";
import BerthForm, { BerthFormInputs } from "./BerthForm";

export type BerthDialogAction = "add" | "edit" | "view";

type Props = {
  activeAction?: BerthDialogAction;
  berthId?: number;
  open?: boolean;
  portId?: number;
  onClose: () => void;
  onAdd?: (berth: BerthCreateDto) => void;
  onEdit?: (berth: BerthEditDto) => void;
};

function formDataToArgs(
  x: BerthFormInputs,
  points: Coordinate[],
): BerthCreateDto | BerthEditDto {
  return {
    ...x,
    capacity: x?.capacity ?? 0,
    water: x?.water ?? false,
    garbage: x?.garbage ?? false,
    power: x?.power ?? false,
    name: x?.name ?? undefined,
    portId: x?.portId ?? -1,
    points: points.map((p) => ({ x: p[0], y: p[1] })),
  };
}

const useStyles = makeStyles((theme) => ({
  mapContainer: {
    height: "90%",
    width: "100%",
    marginBottom: theme.spacing(1),
  },
  drawActionButton: {
    display: "block",
    margin: "0 auto",
    color: theme.palette.warning.contrastText,
    backgroundColor: theme.palette.warning.main,
    "&:hover": {
      backgroundColor: theme.palette.warning.dark,
    },
  },
}));

const berthCreationStyle = (feature: FeatureLike) =>
  new Style({
    fill: new Fill({
      color: "rgba(255, 255, 255, 0.2)",
    }),
    stroke: new Stroke({
      color: "#6ffc03",
      width: 2,
    }),
    image: new CircleStyle({
      radius: 7,
      fill: new Fill({
        color: "#6ffc03",
      }),
    }),
    text: new Text({
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      text: feature.get("name") ?? "",
      // Hidden, for now
      scale: 0,
    }),
  });

const BerthAddEditDialog = ({
  activeAction,
  onClose,
  onAdd,
  onEdit,
  portId,
  berthId,
  open = true,
}: Props): JSX.Element | null => {
  const { t } = useTranslation("berths");
  const actions: BerthDialogAction[] = ["add", "edit", "view"];
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<ol.Map>();

  const [forceUpdate, setForceUpdate] = React.useState<boolean>(false);

  const { layerSource: drawingLayerSource } = useVectorLayer({
    map: mapRef.current,
    forceUpdate,
    layerOptions: React.useMemo(
      () => ({
        style: berthCreationStyle,
      }),
      [],
    ),
  });

  const drawRef = useRef<Draw>(
    new Draw({
      source: drawingLayerSource,
      type: GeometryType.POLYGON,
    }),
  );
  const [drawDone, setDrawDone] = useState(false);
  const classes = useStyles();
  const formId = `${activeAction}-berth-form`;
  const { createMap } = useCreateMap();
  const [points, setPoints] = React.useState<Coordinate[]>([]);
  const nextRef = useRef<() => void>();
  const [selectedPortId, setSelectedPortId] = React.useState<
    number | undefined
  >(portId);

  const [formData, setFormData] = React.useState<
    BerthCreateDto | BerthEditDto | BerthDetailsDto
  >();

  const portApi = usePortApi();
  const berthApi = useBerthApi();

  const isEditing = activeAction === "edit";
  const isCreating = activeAction === "add";
  const isViewing = activeAction === "view";
  const { data: berth, isLoading: isLoadingBerth } = useCustomQuery(
    { key: "berth", id: berthId },
    () => berthApi.getBerth(berthId!),
    { enabled: !!berthId },
  );

  React.useEffect(() => {
    if (!berth) return;
    setFormData(berth);
    if (berth.points) setPoints(berth.points.map((p) => [p.x, p.y]));
  }, [berth]);

  const { data: port } = useCustomQuery(
    { key: "port", id: selectedPortId },
    () => portApi.getPort(selectedPortId!),
    { enabled: !!selectedPortId },
  );

  const { data: berths } = useCustomQuery(
    { key: "berths-for-port", id: selectedPortId },
    () => berthApi.getBerths({ id: selectedPortId! }),
    { enabled: !!selectedPortId },
  );

  const handleClearDrawing = React.useCallback(() => {
    drawingLayerSource?.clear();
    // Re-add the drawing interaction
    if (drawRef.current && (isEditing || isCreating)) {
      setDrawDone(false);
      mapRef.current?.addInteraction(drawRef.current);
    }
  }, [drawingLayerSource, isCreating, isEditing]);

  const steps: DialogStep[] = useMemo(() => {
    if (isLoadingBerth) {
      return [
        {
          title: t("general:loading"),
          content: <LoadingBox />,
        },
      ];
    }

    let title = "";
    switch (activeAction) {
      case "add":
        title = t("fill-info");
        break;
      case "edit":
        title = t("edit-info");
        break;
      case "view":
        title = berth?.name ?? "";
        break;
      default:
        break;
    }

    const lastStep = {
      title: isViewing ? title : t("general:confirm"),
      content: (
        <Grid container>
          <Grid
            item
            xs={4}
            ref={mapContainerRef}
            className={classes.mapContainer}
          />

          <Grid item xs={8}>
            <BerthForm
              small
              onSubmit={() => null}
              formId={formId}
              defaultValues={formData as BerthDetailsDto}
              readOnly
            />
          </Grid>
        </Grid>
      ),
    };

    if (isViewing) return [lastStep];

    return [
      {
        title: t("draw-berth"),
        content: (
          <>
            <div ref={mapContainerRef} className={classes.mapContainer} />
            {isCreating && (
              <BerthPortSelectForm
                fullWidth
                portId={portId}
                onSubmit={(id) => {
                  setSelectedPortId(id);
                }}
              />
            )}
            <Button
              variant="contained"
              classes={{ root: classes.drawActionButton }}
              onClick={handleClearDrawing}
            >
              {t("general:clear")}
            </Button>
          </>
        ),
      },
      {
        title,
        content: (
          <BerthForm
            portId={selectedPortId}
            onSubmit={(d) => {
              setFormData(formDataToArgs(d, points));
              nextRef.current?.();
            }}
            formId={formId}
            defaultValues={formData as BerthDetailsDto}
            readOnly={isViewing}
          />
        ),
      },
      lastStep,
    ];
  }, [
    activeAction,
    berth,
    classes.drawActionButton,
    classes.mapContainer,
    formData,
    formId,
    handleClearDrawing,
    isCreating,
    isLoadingBerth,
    isViewing,
    points,
    portId,
    selectedPortId,
    t,
  ]);

  const handleStepLogic = useCallback(
    (step: number) => {
      setForceUpdate((c) => !c);
      // Add map on step 0 and the required interactions
      if (step !== 0) {
        mapRef.current?.dispose();
        mapRef.current = undefined;
      }

      if (step === 0 && mapContainerRef.current) {
        if (!mapRef.current) {
          mapRef.current = createMap({
            target: mapContainerRef.current,
            includedLayers: [
              LayerTitle.OSM,
              LayerTitle.Berths,
              LayerTitle.Ports,
            ],
            popup: false,
            existingMap: mapRef.current,
          });

          // Set the existing berths layer as visible, ignoring the actual settings
          findLayerByTitle(mapRef.current, LayerTitle.Berths)?.setVisible(true);

          if (!isViewing && points.length < 1) {
            mapRef.current.addInteraction(drawRef.current);
          }

          // Add the points of the currently edited berth

          if (points.length) {
            const feature = new Feature<Polygon>({
              geometry: new Polygon([points]),
              name: berth?.name,
            });

            const ext = feature.getGeometry()?.getExtent();
            if (feature && ext && mapRef.current) {
              drawingLayerSource?.addFeature(feature);
              mapRef.current?.getView().fit(ext);
              setDrawDone(true);
              if (drawRef.current) {
                mapRef.current?.removeInteraction(drawRef.current);
              }
            }
          }

          if (!isViewing) {
            const snap = new Snap({ source: drawingLayerSource });
            mapRef.current.addInteraction(snap);
            const modify = new Modify({ source: drawingLayerSource });
            mapRef.current.addInteraction(modify);
            drawRef.current?.on("drawend", () => {
              setDrawDone(true);
              if (drawRef.current) {
                mapRef.current?.removeInteraction(drawRef.current);
              }
            });
          }
        }
        if (mapRef.current) {
          if (isCreating && drawRef.current) {
            if (selectedPortId === undefined) {
              mapRef.current?.removeInteraction(drawRef.current);
            } else if (
              !mapRef.current
                .getInteractions()
                .getArray()
                .includes(drawRef.current) &&
              points.length < 1
            ) {
              mapRef.current.addInteraction(drawRef.current);
            }
          }

          // Add current port's berths
          addBerthsToMap(mapRef.current, berths?.results ?? []);

          // Add the port we are adding this to
          const portLayer = findLayerByTitle(mapRef.current, LayerTitle.Ports);
          portLayer?.setVisible(true);
          addPortsToMap(mapRef.current, [...(port ? [port] : [])]);

          if (port) {
            const portFeature = (portLayer as VectorLayer)
              ?.getSource()
              ?.getFeatureById(`port${selectedPortId}`);
            const mapView = mapRef.current?.getView();
            const poly = portFeature.getGeometry() as Polygon;
            const ext = poly.getExtent();
            mapView?.fit(ext);
            const curZoom = mapView?.getZoom();
            if (!curZoom) return;
            mapView?.setZoom(curZoom - 0.2);
          }
        }
      }
      if (step === 2 && mapContainerRef.current && !mapRef.current) {
        mapRef.current = createMap({
          target: mapContainerRef.current,
          includedLayers: [LayerTitle.OSM, LayerTitle.Berths],
          popup: false,
          existingMap: mapRef.current,
        });

        // Add a layer for the drawing

        // Set the existing layer as visible, ignoring the actual settings
        findLayerByTitle(mapRef.current, LayerTitle.Berths)?.setVisible(true);
        if (points.length > 0) {
          const polygon = new Polygon([points]);
          const feature = new Feature<Polygon>({
            geometry: polygon,
            name: formData?.name,
          });
          feature.setId("finished");
          drawingLayerSource.addFeature(feature);

          const ext = polygon.getExtent();
          mapRef.current.getView().fit(ext);
          const curZoom = mapRef.current.getView().getZoom();
          if (!curZoom) return;
          mapRef.current.getView().setZoom(curZoom - 0.2);
        }
        mapToReadOnly(mapRef.current);
      }
    },
    [
      berth?.name,
      berths?.results,
      createMap,
      drawingLayerSource,
      formData?.name,
      isCreating,
      isViewing,
      points,
      port,
      selectedPortId,
    ],
  );

  const onDrawDone = useCallback(() => {
    const feat = drawingLayerSource?.getFeatures()[0] as Feature<Polygon>;
    // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle
    const _points = feat?.getGeometry()?.getCoordinates()[0] ?? [];
    setPoints(_points);
  }, [drawingLayerSource]);

  const renderNextButton = useCallback(
    (step, next) => {
      if (isViewing) return null;

      switch (step) {
        case 0:
          return (
            <Button
              variant="contained"
              onClick={(e) => {
                onDrawDone();
                next();
                e.preventDefault();
              }}
              color="secondary"
              disabled={!drawDone}
            >
              {t("general:next")}
            </Button>
          );

        case 1:
          return (
            <Button
              type="submit"
              form={formId}
              variant="contained"
              color="secondary"
            >
              {t("general:next")}
            </Button>
          );
        case 2:
          return (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                if (isCreating) {
                  onAdd?.(formData as BerthCreateDto);
                } else if (isEditing) {
                  onEdit?.(formData as BerthEditDto);
                }
              }}
            >
              {t("general:save")}
            </Button>
          );
        default:
          return null;
      }
    },
    [
      isViewing,
      drawDone,
      t,
      formId,
      onDrawDone,
      isCreating,
      isEditing,
      onAdd,
      formData,
      onEdit,
    ],
  );

  const renderBackButton = useCallback(
    (step, back, handleDialogClose) => {
      return (
        <Button
          onClick={() => {
            if (step === 0) {
              handleDialogClose();
              onClose();
            } else {
              back();
            }
          }}
        >
          {isViewing
            ? t("general:close")
            : t(`general:${step === 0 ? "cancel" : "back"}`)}
        </Button>
      );
    },
    [isViewing, onClose, t],
  );

  if (!activeAction || !actions.includes(activeAction)) return null;

  return (
    <GenericStepperDialog
      open={open}
      steps={steps}
      onClose={onClose}
      dialogTitle=""
      minHeight="40%"
      height="95%"
      contentHeight="80%"
      onStepActive={handleStepLogic}
      renderNextButton={renderNextButton}
      renderBackButton={renderBackButton}
      nextRef={nextRef}
      hideSteps={isViewing || isLoadingBerth}
    />
  );
};

export default BerthAddEditDialog;
